import React, { useMemo } from "react";
import styled from "styled-components";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import {
  tooltipContentGeneratorForUnits,
} from "common/graphs/NVStackedAreaChart";
import { NVStackedAreaChartDownsampled } from "common/graphs/DownSampling";
import { PERIOD_DASHBOARD_TO_HOURS, groupIntoRest } from "./utils";
import useDashboardData from "./useDashboardData";
import Loading from "common/Loading";
import { atBqnTarget, safeStr } from "common/api";
import {parseThroughputPerPolicyIgnoringSubscribers} from "views/Statistics/Throughput/PerPolicy/api";
import * as d3 from "d3";

const Wrapper = styled.div`
  width: 100%;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: var(--text-primary);
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

const getPoliciesThroughputData = ({ hours, mode, bqnId, type, range }) =>
  ifCl
    .run(
      atBqnTarget(
        `show statistics policy ${safeStr(type)} speed lines ${hours * 12}`,
        mode,
        bqnId
      )
    )
    .then((response) => ({
      data: parseThroughputPerPolicyIgnoringSubscribers(response),
      range,
    }));

function PoliciesThroughput({ type, period, range }) {
  const colorsSet = login.isTheme("light") ? colors : colorsDark;

  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getPoliciesThroughputData({
        hours: PERIOD_DASHBOARD_TO_HOURS[period],
        mode,
        bqnId,
        type,
        range,
      }),
    onError: "Error fetching policies throughput data:",
    deps: [period, type],
    range,
  });

  const { data, range: rangeDrawn } = dataRange || {};
  const { timeline, participants } = data || {};

  const parsedItems = useMemo(() => {
    return timeline ? timeline.map((item) => {
      const policies = item.policies.reduce((acc, policy) => {
        acc[policy.name] = policy.downSpeed;
        return acc;
      }, {});
      return {
        time: item.time,
        ...policies,
      };
    }) : [];
  }, [timeline]);

  // A BQN of many policies would fill the card with the legend of the chart,
  // so the ones left over the items it holds are drawn as one area of their
  // own, which adds up the throughput of all of them.
  const { participants: policiesShown, items: itemsShown } = useMemo(
    () => groupIntoRest(participants || [], parsedItems, 9),
    [participants, parsedItems]
  );

  const fields = useMemo(() => {
    return policiesShown.map((p, i) => ({
      name: p,
      label: p,
      color: colorsSet[i % colorsSet.length],
    }));
  }, [policiesShown, colorsSet]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <WrapperLoader>
        No data available
      </WrapperLoader>
    );
  }

  return (
    <Wrapper>
      <Subtitle>Downlink</Subtitle>
      <NVStackedAreaChartDownsampled
        copyEndpoints={true}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        xField="time"
        fields={fields}
        items={itemsShown}
        range={rangeDrawn}
        yAxisUnits={"Mbps"}
        yAxisFormat={d3.format(".1f")}
        tooltipContentGenerator={tooltipContentGeneratorForUnits("Mbps")}
        yAxisTopGap={0.05}
        margin={{ top: 5, right: 10, bottom: 20, left: 60 }}
        xAxisTicksNumber={4}
      />
    </Wrapper>
  );
}

export default React.memo(PoliciesThroughput);
